import { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import Modal from "@/components/Modal";
import { fmtDate } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { Camera, Download, Trash2, X, UploadCloud, Pencil, Smartphone, ChevronLeft, ChevronRight } from "lucide-react";

function useAuthImage(photoId, open) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    if (!open || !photoId) return undefined;
    let url;
    api.get(`/site-photos/${photoId}/file`, { responseType: "blob" }).then((r) => {
      url = URL.createObjectURL(r.data);
      setSrc(url);
    }).catch(() => setSrc(null));
    return () => url && URL.revokeObjectURL(url);
  }, [photoId, open]);
  return src;
}

function Thumb({ photo, onOpen }) {
  const src = useAuthImage(photo.id, true);
  return (
    <button data-testid={`site-photo-thumb-${photo.id}`} onClick={onOpen}
      className="h-24 w-full overflow-hidden rounded-md border border-gray-200 bg-gray-100 transition-opacity hover:opacity-85">
      {src ? <img src={src} alt={photo.description || photo.filename} className="h-full w-full object-cover" /> : <Camera size={18} className="mx-auto text-gray-300" />}
    </button>
  );
}

function Viewer({ photos, index, onIndex, onClose }) {
  const photo = photos[index];
  const src = useAuthImage(photo.id, true);
  const hasPrev = index > 0;
  const hasNext = index < photos.length - 1;
  const multi = photos.length > 1;
  const download = async () => {
    const r = await api.get(`/site-photos/${photo.id}/file`, { responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    const a = document.createElement("a");
    a.href = url; a.download = photo.filename || "site-photo";
    a.click(); URL.revokeObjectURL(url);
  };
  useEffect(() => {
    const onKey = (e) => {
      const t = e.target;
      if (t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.tagName === "SELECT" || t.isContentEditable)) return;
      if (e.key === "Escape") { e.preventDefault(); onClose(); }
      else if (e.key === "ArrowRight" && hasNext) { e.preventDefault(); onIndex(index + 1); }
      else if (e.key === "ArrowLeft" && hasPrev) { e.preventDefault(); onIndex(index - 1); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [index, hasPrev, hasNext, onIndex, onClose]);
  const navBtn = "flex h-8 w-8 items-center justify-center rounded-md border border-gray-200 text-gray-500 transition-colors hover:text-gray-950 disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:text-gray-500";
  return createPortal(
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/70 p-4" onClick={onClose} data-testid="site-photo-viewer" data-photo-id={photo.id} data-photo-index={index}>
      <div className="flex max-h-[calc(100vh-2rem)] w-full max-w-4xl flex-col overflow-hidden rounded-lg bg-white" onClick={(e) => e.stopPropagation()}>
        <div className="flex shrink-0 items-center justify-between gap-3 border-b border-gray-200 bg-gray-50 px-5 py-3">
          <div className="min-w-0">
            <div className="truncate text-sm font-semibold text-gray-950" data-testid="site-photo-viewer-title">{photo.description || photo.filename}</div>
            <div className="text-[11px] text-gray-500">Uploaded {fmtDate(photo.uploaded_at)} by {photo.uploaded_by}{multi ? <span className="ml-2 font-mono-data text-gray-400" data-testid="site-photo-viewer-position">{index + 1} / {photos.length}</span> : null}</div>
          </div>
          <div className="flex items-center gap-2">
            {multi && (
              <>
                <button data-testid="site-photo-prev" onClick={() => onIndex(index - 1)} disabled={!hasPrev} className={navBtn} title="Previous photo (←)" aria-label="Previous photo"><ChevronLeft size={16} /></button>
                <button data-testid="site-photo-next" onClick={() => onIndex(index + 1)} disabled={!hasNext} className={navBtn} title="Next photo (→)" aria-label="Next photo"><ChevronRight size={16} /></button>
              </>
            )}
            <button data-testid="site-photo-download" onClick={download} className="bdx-btn-secondary flex items-center gap-1.5"><Download size={14} /> Download</button>
            <button data-testid="site-photo-viewer-close" onClick={onClose} className={navBtn}><X size={15} /></button>
          </div>
        </div>
        <div className="min-h-0 flex-1 overflow-auto bg-gray-900/95 p-3">
          {src ? <img src={src} alt={photo.description || photo.filename} className="mx-auto max-h-[70vh] object-contain" /> : <div className="py-16 text-center text-sm text-gray-400">Loading…</div>}
        </div>
      </div>
    </div>,
    document.body
  );
}

export default function SitePhotos({ project, roadmap }) {
  const { readOnly } = useAuth();
  const [photos, setPhotos] = useState([]);
  const [open, setOpen] = useState(false);
  const [viewing, setViewing] = useState(null); // index into the displayed (category-scoped) thumbnail order
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => api.get(`/projects/${project.id}/site-photos?roadmap=${roadmap}`).then((r) => setPhotos(r.data)), [project.id, roadmap]);
  useEffect(() => { load(); }, [load]);

  const upload = async (files, mode = "original") => {
    const list = Array.from(files || []);
    if (!list.length) return;
    setBusy(true);
    let ok = 0;
    try {
      for (const file of list) {
        const fd = new FormData();
        fd.append("file", file); fd.append("roadmap", roadmap); fd.append("upload_mode", mode);
        await api.post(`/projects/${project.id}/site-photos`, fd);
        ok += 1;
      }
      toast.success(`${ok} site photo${ok > 1 ? "s" : ""} uploaded${mode === "phone_optimized" ? " (optimised)" : ""}`);
    } catch (e) {
      toast.error(`${apiError(e)}${ok ? ` — ${ok} photo(s) uploaded before the error` : ""}`);
    } finally {
      setBusy(false);
      load();
    }
  };

  const editDesc = async (photo) => {
    const next = window.prompt("Photo description", photo.description || "");
    if (next === null) return;
    try { await api.patch(`/site-photos/${photo.id}`, { description: next }); toast.success("Description updated"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const remove = async (photo) => {
    if (!window.confirm("Delete this site photo? The image will be removed from the project.")) return;
    try { await api.delete(`/site-photos/${photo.id}`); toast.success("Site photo deleted"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <>
      <button data-testid={`site-photos-btn-${roadmap}`} onClick={() => setOpen(true)}
        className="flex h-9 shrink-0 items-center gap-2 rounded-md border border-blue-300 bg-blue-50 px-3 text-[11px] font-bold uppercase text-blue-700 shadow-sm transition-colors hover:border-blue-400 hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-blue-300"
        style={{ letterSpacing: "0.05em" }} title="Open Site Photos">
        <Camera size={14} strokeWidth={1.8} /> Site Photos{photos.length > 0 ? ` · ${photos.length}` : ""}
      </button>
      {open && (
        <Modal title={`Site Photos — ${roadmap === "nbn" ? "NBN" : roadmap.charAt(0).toUpperCase() + roadmap.slice(1)} Roadmap`} onClose={() => setOpen(false)} wide>
          {!readOnly && (
            <div className="mb-5 rounded-md border border-dashed border-gray-300 bg-gray-50 p-4">
              <div className="bdx-label mb-1.5">Add Site Photos (JPG / PNG / WEBP — select multiple)</div>
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                <label className={`bdx-btn-primary flex shrink-0 cursor-pointer items-center gap-2 ${busy ? "opacity-50" : ""}`} title="Optimises mobile photos (~30% dimensions)">
                  <Smartphone size={15} /> {busy ? "Uploading…" : "Phone Upload"}
                  <input data-testid="site-photo-phone-input" type="file" multiple accept=".jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp" className="hidden" disabled={busy}
                    onChange={(e) => { upload(e.target.files, "phone_optimized"); e.target.value = ""; }} />
                </label>
                <label className={`bdx-btn-secondary flex shrink-0 cursor-pointer items-center gap-2 ${busy ? "opacity-50" : ""}`} title="Original resolution">
                  <UploadCloud size={15} /> Upload
                  <input data-testid="site-photo-file-input" type="file" multiple accept=".jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp" className="hidden" disabled={busy}
                    onChange={(e) => { upload(e.target.files, "original"); e.target.value = ""; }} />
                </label>
                <p className="text-[11px] text-gray-500">Phone Upload optimises mobile photos · Upload keeps original resolution. Descriptions can be added per photo after upload.</p>
              </div>
            </div>
          )}
          {photos.length === 0 ? (
            <div className="py-10 text-center text-sm text-gray-400" data-testid="site-photos-empty">No site photos have been added to this roadmap</div>
          ) : (
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3" data-testid="site-photos-grid">
              {photos.map((ph) => (
                <div key={ph.id} className="rounded-lg border border-gray-200 bg-white p-2.5 shadow-sm">
                  <Thumb photo={ph} onOpen={() => setViewing(photos.indexOf(ph))} />
                  <div className="mt-2 truncate text-xs font-semibold text-gray-900">{ph.description || ph.filename}</div>
                  <div className="mt-0.5 text-[10px] text-gray-500">{fmtDate(ph.uploaded_at)} · {ph.uploaded_by}</div>
                  {!readOnly && (
                    <div className="mt-2 flex gap-1 border-t border-gray-100 pt-2">
                      <button data-testid={`site-photo-edit-${ph.id}`} onClick={() => editDesc(ph)} className="flex h-7 w-7 items-center justify-center rounded text-gray-400 hover:bg-gray-100 hover:text-gray-900" title="Edit description"><Pencil size={12} /></button>
                      <button data-testid={`site-photo-delete-${ph.id}`} onClick={() => remove(ph)} className="flex h-7 w-7 items-center justify-center rounded text-gray-400 hover:bg-red-50 hover:text-red-600" title="Delete photo"><Trash2 size={12} /></button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </Modal>
      )}
      {viewing !== null && photos[viewing] && <Viewer photos={photos} index={viewing} onIndex={setViewing} onClose={() => setViewing(null)} />}
    </>
  );
}
